import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import debounce from 'lodash.debounce';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { getChartTypeMenuItems } from '../../../Components/Menu/MenuItemsHelper';
import { chartSkeletonScreen, mobileChartSkeletonScreen } from '../../../SkeletonScreens/SkeletonScreen';
import { denormalizeRecordings } from '../../../Reducks/Recordings/RecordingsUtils';
import './RecordingsChartContainer.css';
import { getTrimString } from '../../../Utils/StringUtils';
import { CSV, HIGHCHARTS_MENU, PNG, XLS, RecordingChartType } from '../../../Utils/Constants';
import applyExporting from 'highcharts/modules/exporting';
import applyOffline from 'highcharts/modules/offline-exporting';
import applyExportData from 'highcharts/modules/export-data';
import Menu from '../../../Components/Menu/Menu';
import {
  DOWNLOAD_CHART,
  MixpanelChartType,
  PNG as mixpanel_PNG,
  CSV as mixpanel_CSV,
  EXCEL as mixpanel_EXCEL,
  RECORDINGS_OVERVIEW,
  VALUE_ADD
} from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import { trackAnalyticsChartType, trackAnalyticsFeature } from '../../../Telemetry/Mixpanel/Features/analyticsTracker';
import DownloadButton from '../../../Components/Buttons/DownloadIcon';
import TabBar from '../../../Components/TabBar/TabBar';
import { getRecordingSource } from '../Utils/RecordingsContainerUtils';

export class RecordingsChartContainer extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };
  static propTypes = {
    recordings: PropTypes.object,
    user: PropTypes.object
  };
  constructor(props) {
    super(props);
    this.chartRef = createRef();
    this.filteredSelectedRecordings = [];
    this.state = {
      activeTab: RecordingChartType.VIEWS
    };
  }
  componentDidMount() {
    applyExporting(Highcharts);
    applyOffline(Highcharts);
    applyExportData(Highcharts);
  }
  componentDidUpdate() {
    this.hoverOnRecording(this.props.focusedRecordingKeyInChart);
  }

  onClickHandler = id => {
    let type = MixpanelChartType.RECORDINGS_OVERVIEW_VIEWS_CHART;
    if (this.state.activeTab === RecordingChartType.SHARES) {
      type = MixpanelChartType.RECORDINGS_OVERVIEW_SHARES_CHART;
    }
    trackAnalyticsFeature(VALUE_ADD, RECORDINGS_OVERVIEW, DOWNLOAD_CHART);
    if (id === PNG) {
      this.chartRef.current.chart.exportChartLocal();
      trackAnalyticsChartType(type, mixpanel_PNG);
    } else if (id === XLS) {
      this.chartRef.current.chart.downloadXLS();
      trackAnalyticsChartType(type, mixpanel_EXCEL);
    } else if (id === CSV) {
      this.chartRef.current.chart.downloadCSV();
      trackAnalyticsChartType(type, mixpanel_CSV);
    }
  };

  render() {
    const { fetching, recordings, filter } = this.props.recordings;
    const { isBig, user } = this.props;
    const focusRecordingInTable = this.props.focusRecordingInTable;
    const { filteredSelectedRecordingKeys, byKey } = recordings;
    const { fromTime: fromTimeString, toTime: toTimeString } = filter;
    const fromTime = moment(fromTimeString);
    const toTime = moment(toTimeString);

    const { t } = this.context;
    let recordingsCount = '5',
      publishedDate = fromTime.format(t('formatting.date'));
    let fromDate = fromTime.format(t('formatting.date')),
      toDate = toTime.format(t('formatting.date'));

    let chartTitleText;
    if (this.state.activeTab === RecordingChartType.VIEWS) {
      chartTitleText = t('recordings.topNViewedRecordings', { recordingsCount, fromDate, toDate });
      if (fromTime.year() === toTime.year() && fromTime.dayOfYear() === toTime.dayOfYear()) {
        chartTitleText = t('recordings.topNViewedRecordingsSingle', { recordingsCount, publishedDate });
      }
    } else {
      chartTitleText = t('recordings.topNSharedRecordings', { recordingsCount, fromDate, toDate });
      if (fromTime.year() === toTime.year() && fromTime.dayOfYear() === toTime.dayOfYear()) {
        chartTitleText = t('recordings.topNSharedRecordingsSingle', { recordingsCount, publishedDate });
      }
    }

    this.filteredSelectedRecordingsViews = this.getTop5RecordingByViews(
      denormalizeRecordings(filteredSelectedRecordingKeys, byKey)
    );
    this.filteredSelectedRecordingsShares = this.getTop5RecordingByShares(
      denormalizeRecordings(filteredSelectedRecordingKeys, byKey)
    );
    const filteredSelectedKeys =
      this.state.activeTab === RecordingChartType.VIEWS
        ? this.filteredSelectedRecordingsViews
        : this.filteredSelectedRecordingsShares;

    const detailsMenu = [];
    const menuItems = getChartTypeMenuItems(HIGHCHARTS_MENU);
    menuItems.map(item =>
      detailsMenu.push({
        onClick: () => this.onClickHandler(item.id),
        text: item.title,
        iconClassName: item.iconClass,
        id: item.id
      })
    );

    const tabOptions = [
      { id: RecordingChartType.VIEWS, title: t('tableTopStat.views') },
      { id: RecordingChartType.SHARES, title: t('tableTopStat.shares') }
    ].map(option => {
      return { ...option, onClick: () => this.setState({ activeTab: option.id }) };
    });

    return (
      <div>
        {fetching ? (
          isBig ? (
            chartSkeletonScreen()
          ) : (
            mobileChartSkeletonScreen()
          )
        ) : (
          <div className="recordingsChartLayout">
            <div className="panelHeader">
              <div className="panelTitle">
                <TabBar activeTabId={this.state.activeTab} tabContentMetadata={tabOptions} />
              </div>
              <Menu
                isBig={isBig}
                menuKey="recordingsChartDownload"
                buttonName={t('buttons.downloadCamelCase')}
                detailsMenu={detailsMenu}
                tooltipText={t('chart.downloadTooltipText')}
                buttonComponent={<DownloadButton isBig={isBig} />}
              />
            </div>
            <div className="panelBody">
              <HighchartsReact
                ref={this.chartRef}
                highcharts={Highcharts}
                constructorType={'chart'}
                options={this.getChartOptions(
                  filteredSelectedKeys,
                  focusRecordingInTable,
                  isBig,
                  this.state.activeTab,
                  chartTitleText,
                  user
                )}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  getChartOptions = (recordings, focusRecordingInTable, isBig, type, chartTitleText, user) => {
    const darkMode = user && user.darkMode;
    const { t } = this.context;
    return {
      colors: type === RecordingChartType.VIEWS ? ['#0c9ae7'] : ['#18e4b6'],
      chart: {
        type: 'bar',
        height: isBig ? 350 : 450,
        zoomType: 'x',
        backgroundColor: darkMode ? '#444444' : '#ffffff'
      },
      legend: {
        enabled: false
      },
      title: {
        text: chartTitleText,
        align: 'left',
        style: {
          color: darkMode ? '#ffffff' : '#444444',
          fontFamily: 'Lato',
          fontSize: '18px'
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false,
        csv: {
          decimalPoint: null
        },
        type: 'image/png',
        url: 'https://export.highcharts.com/'
      },
      xAxis: {
        tickWidth: 0,
        lineWidth: 0,
        labels: {
          enabled: isBig,
          align: 'left',
          reserveSpace: true,
          color: '#4a4a4a',
          style: {
            fontWeight: 500,
            fontFamily: 'Lato',
            fontSize: '16px',
            letterSpacing: 0,
            color: darkMode ? '#ffffff' : '#666666'
          }
        },
        categories: this.getXAxisCountArray(recordings),
        crosshair: false
      },
      yAxis: [
        {
          gridLineWidth: 0,
          lineWidth: 1,
          min: 0,
          title: '',
          tickAmount: 5,
          allowDecimals: false,
          labels: {
            style: {
              color: darkMode ? '#ffffff' : '#666666'
            }
          }
        }
      ],
      tooltip: isBig
        ? {
            useHTML: true,
            shared: true,
            formatter: RecordingsChartContainer.formatTooltip,
            borderRadius: '2',
            borderWidth: '0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 5px 15px 0 rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode ? '#2A2A2A' : '#ffffff',
            style: {
              color: darkMode ? '#ffffff' : '#444444',
              fontSize: '0.75em',
              fontFamily: 'Lato, sans-serif'
            }
          }
        : { enabled: false },
      plotOptions: {
        series: {
          borderColor: darkMode ? '#444444' : '#ffffff',
          turboThreshold: 2000,
          maxPointWidth: 50,
          point: {
            events: {
              mouseOver: debounce(function() {
                if (this.options && this.options.recordingKey) {
                  focusRecordingInTable(this.options.recordingKey);
                }
              }, 150),
              mouseOut: debounce(function() {
                focusRecordingInTable(null);
              }, 150)
            }
          },
          animation: {
            duration: 500,
            enabled: true
          }
        },
        line: {
          marker: {
            enabled: false
          }
        }
      },
      series: [
        {
          dataLabels: {
            enabled: !isBig,
            align: 'left',
            inside: true,
            x: -4,
            y: -24,
            format: '{point.recordingName}',
            style: {
              fontFamily: 'Lato',
              fontSize: '14px',
              fontWeight: 500,
              fontStyle: 'normal',
              fontStretch: 'normal',
              lineHeight: 'normal',
              letterSpacing: 'normal',
              color: '#4a4a4a'
            }
          },
          pointWidth: isBig ? null : 16,
          name: type === RecordingChartType.VIEWS ? t('tableTopStat.views') : t('tableTopStat.shares'),
          data: this.getRecordingsCountArray(recordings, type),
          states: {
            hover: {
              color: type === RecordingChartType.VIEWS ? '#0869a0' : '#13b48d'
            }
          }
        }
      ]
    };
  };

  getFormattedStartTime = (startTime, timeZone) => {
    timeZone = 'UTC';
    const { t } = this.context;
    return moment(startTime)
      .tz(timeZone)
      .format(t('formatting.fullDayDate'));
  };

  getRecordingsCountArray = (recordings, type) => {
    const { t } = this.context;
    if (!recordings) {
      return [];
    }
    const countArray = [];
    recordings.forEach(data => {
      countArray.push({
        y: type === RecordingChartType.VIEWS ? data.viewCount : data.shareCount,
        recordingName: getTrimString(data.title, 40),
        recordingKey: data.recordingKey,
        sourceName: t('tableHeaderColumns.source'),
        source: getRecordingSource(data.source),
        dateUploaded: this.getFormattedStartTime(data.dateUploaded, null)
      });
    });
    return countArray;
  };

  getXAxisCountArray = recordings => {
    if (!recordings) {
      return [];
    }
    return recordings.map(data => getTrimString(data.title, 24));
  };

  static formatTooltip(tooltip, x = this.x, points = this.points) {
    let { dateUploaded, recordingName } = points[0].point;
    return `<br/>
      <div style="font-size: 16px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: 1;
      letter-spacing: normal; padding-bottom: 6px;">
        ${recordingName}
      </div>
      <div style="font-size: 14px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">
        ${dateUploaded}
      </div>
      <hr style="border-top: 1px solid #d0d2dd; margin-top: 11px; margin-bottom: 16px;" />
      <div style="display: flex; flex-direction: row; justify-content: space-between; padding-bottom: 16px;">
        <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
        letter-spacing: normal;">${points[0].point.sourceName}</div>
        <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
        letter-spacing: normal;">${points[0].point.source}</div>
      </div>
      <div style="display: flex; flex-direction: row; justify-content: space-between;">
        <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
        letter-spacing: normal;">${points[0].series.name}</div>
        <div style="font-size: 18px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: normal;
        letter-spacing: normal;">${points[0].point.y}</div>
      </div>
      `;
  }

  hoverOnRecording = focusedRecordingKeyInChart => {
    try {
      let series = this.chartRef.current.chart.series;
      if (focusedRecordingKeyInChart) {
        let i = this.filteredSelectedRecordings.findIndex(x => x.recordingKey === focusedRecordingKeyInChart);
        let tooltipArray = [series[0]]
          .filter(a => a)
          .map(b => b.data[i])
          .filter(k => k);
        this.chartRef.current.chart.tooltip.refresh(tooltipArray);
      } else {
        this.chartRef.current.chart.tooltip.hide();
      }
    } catch (e) {}
  };

  getTop5RecordingByViews(recordings) {
    if (recordings) {
      return recordings.sort((a, b) => b.viewCount - a.viewCount).slice(0, 5);
    }
  }

  getTop5RecordingByShares(recordings) {
    if (recordings) {
      return recordings.sort((a, b) => b.shareCount - a.shareCount).slice(0, 5);
    }
  }
}

export default connect(state => ({ recordings: state.reduced_recordings, user: state.reduced_user }))(
  RecordingsChartContainer
);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Recordings/Chart/RecordingsChartContainer.js